import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl }                                              from "react-intl";
import { CustomerTemplate, FieldError, FieldInfo, LanguageMap } from "../types";
import { useTranslations }                                      from "../providers/translations-provider";

export interface FieldDataValue {
    value: string,
    dirty: boolean,
    valid: boolean,
    errors: FieldError[]
}

export type FieldData = {
    [k: string]: FieldDataValue;
};

export type SignatureMap = { [id: string]: SignatureField }

export interface SignatureField {
    id: number;
    sort: number;
    signature: string;
    subtitle: string;
}

export interface TemplateData {
    key: string;
    name: LanguageMap;
    defaultValue: any;
    maxLength: number;
    info: FieldInfo;
}

let signatureID = 0;

export const useCustomerTemplates = (initialTemplates: CustomerTemplate[]) => {

    const reduceTemplateDefaults = (data: TemplateData[]) => {
        return data.reduce<FieldData>((stack, { key, defaultValue, info }) => {
            const errors = (info && info.isValid)
                ? info.isValid(defaultValue, info)
                : [];
            stack[key] = {
                value: defaultValue,
                dirty: false,
                valid: errors.length === 0,
                errors
            };
            return stack;
        }, {})

    }

    const reduceTemplateData = (template: CustomerTemplate, language: string) => {
        return template.templateData.map<TemplateData>((info) => {
            const { key, name, languageData, maxLength } = info;
            return {
                key,
                name,
                defaultValue: languageData[language],
                maxLength,
                info,
            }
        })
    }

    const { currentLanguage } = useTranslations();
    const initialTemplate = initialTemplates.length > 0 ? initialTemplates[0] : null;
    const initialLanguage = initialTemplate && initialTemplate.languages.some(l => l === currentLanguage) ? currentLanguage : 'en';
    const [selectedTemplate, setSelectedTemplate] = useState<CustomerTemplate | null>(initialTemplate);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(initialLanguage);
    const [fields, setFieldData] = useState<FieldData>(initialTemplate ? reduceTemplateDefaults(reduceTemplateData(initialTemplate, initialLanguage)) : {});
    const [signaturesMap, setSignatures] = useState<SignatureMap>({ });

    const intl = useIntl();

    const addSignature = () => {
        if (selectedTemplate?.signatureInfo) {
            const len = Object.keys(signaturesMap).length;
            if (!selectedTemplate?.signatureInfo?.maxSignatures || len < selectedTemplate?.signatureInfo?.maxSignatures) {
                const id = signatureID++;
                setSignatures({
                    ...signaturesMap,
                    [id]: {
                        id,
                        signature: '',
                        subtitle: '',
                        sort: len,
                    }
                });
            }
        }
    };
    const updateSignature = (id: number) => (fieldName: 'signature' | 'subtitle') => (event: any) => {
        const elem = signaturesMap[id];
        if (elem) {
            elem[fieldName] = event.target.value.substr(0, selectedTemplate?.signatureInfo?.maxLength);
            setSignatures({
                ...signaturesMap,
                [id]: elem
            })
        }
    };

    const removeSignature = (id: number) => {
        const { [id]: toRemove, ...signatures } = signaturesMap;
        setSignatures(signatures);
    };

    const swapSignature = (id: number, otherId: number) => {
        const a = signaturesMap[id];
        const b = signaturesMap[otherId];

        if(a && b) {
            const tmp = b.sort;
            b.sort = a.sort;
            a.sort = tmp;

            setSignatures({
                ...signaturesMap,
                [a.id]: a,
                [b.id]: b
            })
        }
    }

    const signatures = useMemo(() => {
        return Object.values(signaturesMap).sort((a, b) => {
            if (a.sort === b.sort) {
                return 0;
            }
            if (a.sort > b.sort) {
                return 1;
            } else {
                return -1;
            }
        });
    }, [signaturesMap]);

    const overwriteFieldData = () => {
        if (templateData) {
            setFieldData(reduceTemplateDefaults(templateData));
        }
    }

    const languages = useMemo(() => {
        if (!selectedTemplate) {
            return [];
        }

        return selectedTemplate.languages.map((key) => {
            return {
                key,
                name: intl.formatMessage({ id: `__lang.${ key }` })
            }
        })
    }, [selectedTemplate, currentLanguage]);

    const templateData = useMemo(() => {
        if (selectedTemplate && selectedLanguage) {
            return reduceTemplateData(selectedTemplate, selectedLanguage);
        }
        return null;
    }, [selectedTemplate, selectedLanguage]);

    const isFieldsDirty = useMemo(() => {
        return Object.values(fields).some(val => val.dirty);
    }, [fields]);

    useEffect(() => {
        overwriteFieldData();
    }, [templateData]);

    const setTemplate = useCallback((event: any) => {
        const t = initialTemplates.find(t => t.key === event.target.value) || null;
        if (t?.signatureInfo?.maxSignatures ) {
          let count = 0;
          const max = t?.signatureInfo?.maxSignatures;
          setSignatures(Object.keys(signaturesMap).reduce((acc, sig) => {
            if (count < max) {
              acc[sig] = {
                ...signaturesMap[sig],
                signature: signaturesMap[sig].signature.substr(0, t.signatureInfo?.maxLength),
              };
              count++;
            }
            return acc;
          }, {} as SignatureMap));
        }
        setSelectedTemplate(t);
    }, [signatures]);

    const setLanguage = (event: any) => {
        const l = languages.find(l => l.key === event.target.value);
        setSelectedLanguage(l ? l.key : '');

        overwriteFieldData();
    }

    const setField = (key: string) => (event: any) => {
        const templateField = templateData?.find(t => t.key === key);
        const val = event.target.value.substr(0, templateField?.maxLength);

        const errors = (templateField && templateField.info.isValid)
            ? templateField.info.isValid(val, templateField.info)
            : [];

        setFieldData({
            ...fields,
            [key]: {
                value: val,
                dirty: templateField
                    ? templateField.defaultValue !== val
                    : true,
                valid: errors.length === 0,
                errors
            }
        })
    }

    return {
        templates: initialTemplates,
        languages,
        selectedTemplate,
        selectedLanguage,
        setTemplate,
        setLanguage,
        templateData,
        fields,
        setField,
        isFieldsDirty,
        signatures,
        addSignature,
        updateSignature,
        removeSignature,
        swapSignature
    }
}